import { ErrorMessage } from '@hookform/error-message';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Divider } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { css, styled as muiStyled, useTheme } from '@mui/material/styles';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import MicrosoftLoginButton from './microsoft-login-button';
import OktaLoginButton from './okta-login-button';
import SignInSkeleton from './sign-in-skeleton';
import SmallForm from './small-form';
import VerificationModal from '../settings/2fa-auth/otp-verification-modal';

import Arranger from '~c/components/base/arranger';
import Button from '~c/components/base/button';
import { GoogleIcon } from '~c/components/base/icon/google';
import LogoSelector from '~c/components/base/icon/logo-selector';
import Link from '~c/components/base/link';
import Spacer, { spc } from '~c/components/base/spacer';
import Text from '~c/components/base/text';
import TextField from '~c/components/base/text-field';
import { rememberOAuthReturn } from '~c/helpers/pending-oauth-return';
import {
  muiError,
  useYupValidationResolver,
} from '~c/hooks/yup-validation-resolver';
import { nodeAuthApi } from '~c/services';

// TypeScript declaration for Google Identity Services
declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: {
          cancel: () => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}

interface InputData {
  email: string;
  password: string;
  loginResult: string;
}

const validationSchema = yup.object().shape({
  email: yup.string().trim().required(),
  password: yup.string().trim().required(),
});

interface Props {
  className?: string;
}

// Custom Google Button Component
const CustomGoogleButton: React.FC<{
  onClick: () => void;
  loading: boolean;
}> = ({ onClick, loading }) => (
  <Button
    onClick={onClick}
    loading={loading}
    fullWidth
    variant="outlined"
    sx={{ mt: spc.xs, mb: spc.between_vertical_s }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <GoogleIcon />
      <div>Continue with Google</div>
    </div>
  </Button>
);

// Google Login Hook Component
const GoogleLoginButton: React.FC<{
  onSuccess: (tokenResponse: any) => void;
  onError: () => void;
  loading: boolean;
}> = ({ onSuccess, onError, loading }) => {
  const login = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      onSuccess(tokenResponse);
    },
    onError: () => {
      onError();
    },
    flow: 'implicit', // This returns access_token
  });

  // Cleanup effect: Cancel any pending Google One Tap prompts when unmounting
  // This prevents the random OAuth dropdown from appearing after navigating away
  React.useEffect(() => {
    return () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.cancel();
      }
    };
  }, []);

  return <CustomGoogleButton onClick={() => login()} loading={loading} />;
};

const SignInImpl: React.FC<Props> = ({ className }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [googleClientId, setGoogleClientId] = useState<string>('');
  const [signIn, { isLoading }] = nodeAuthApi.useLoginMutation();
  const [googleSignIn, { isLoading: isGoogleLoading }] =
    nodeAuthApi.useGoogleLoginMutation();
  const [microsoftSignIn, { isLoading: isMicrosoftLoading }] =
    nodeAuthApi.useMicrosoftLoginMutation();
  const [oktaSignIn] = nodeAuthApi.useOktaLoginMutation();
  const [fetchSsoDiscover] = nodeAuthApi.useLazySsoDiscoverQuery();

  // SSO discovery state
  const [ssoStep, setSsoStep] = useState<'email' | 'credentials'>('email');
  const [discoveredProviders, setDiscoveredProviders] = useState<
    { type: string; config?: Record<string, string> }[]
  >([]);
  const [discoveredEmail, setDiscoveredEmail] = useState('');
  const [isDiscovering, setIsDiscovering] = useState(false);

  // 2FA state
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [otp, setOtp] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [twoFAError, setTwoFAError] = useState('');
  const [pendingLoginData, setPendingLoginData] = useState<{
    username: string;
    password: string;
  } | null>(null);
  const [pendingMicrosoftIdToken, setPendingMicrosoftIdToken] = useState<
    string | null
  >(null);
  const [pendingOktaIdToken, setPendingOktaIdToken] = useState<string | null>(
    null
  );

  // Every sign-in path (password, SSO, 2FA) ends at organization selection, which returns here if this was set.
  useEffect(() => {
    rememberOAuthReturn(
      new URLSearchParams(window.location.search).get('referrer')
    );
  }, []);

  const navigateAfterLogin = async () => {
    const params = new URLSearchParams(location.search);
    const invitation = params.get('invitation');

    if (invitation) {
      navigate(`/accept-invitation?token=${invitation}`);
      return;
    }

    navigate('/select-organization');
  };

  const resolver = useYupValidationResolver(validationSchema);
  const {
    register,
    formState,
    setError,
    clearErrors,
    handleSubmit,
    getValues,
  } = useForm<InputData>({
    resolver,
  });
  const [showPassword, setShowPassword] = React.useState(false);

  const prefersDarkMode = theme.palette.mode === 'dark';

  const { errors } = formState;

  const toggleShowPassword = () => setShowPassword((show) => !show);

  useEffect(() => {
    const googleClientKey = window.__ENV__.GOOGLE_CLIENT_ID;
    setGoogleClientId(googleClientKey);
  }, []);

  const handleGoogleSuccess = async (tokenResponse: any) => {
    try {
      // With useGoogleLogin, we get an access_token instead of credential
      // We need to use this access_token to get user profile info
      const response = await fetch(
        `https://www.googleapis.com/oauth2/v2/userinfo?access_token=${tokenResponse.access_token}`
      );
      const userInfo = await response.json();

      if (!response.ok) {
        throw new Error('Failed to get user info from Google');
      }

      // Now call your backend with the user info
      const result = await googleSignIn({
        userInfo,
        access_token: tokenResponse.access_token,
      });

      if ('data' in result) {
        const { data } = result;

        if (!data.email_verified) {
          setError('loginResult', { message: 'User email not verified' });
        } else if (!data.is_active) {
          setError('loginResult', {
            message: 'Your account is not activated yet by the admin.',
          });
        } else if (data.otpEnabled) {
          setPendingLoginData({
            username: data.user_email ?? '',
            password: 'google-sso',
          });
          setShow2FAModal(true);
        } else {
          navigateAfterLogin();
        }
      } else if ('error' in result) {
        const errorResponse = result.error as {
          data?: {
            error?: string;
            message?: string;
            payload?: { message?: string };
          };
          status?: number;
          message?: string;
        };

        // Extract the specific error message from different possible locations
        // Boom errors have the message in data.message or data.payload.message
        let errorMessage = 'Google sign-in failed. Please try again.';

        if (errorResponse.data?.message) {
          errorMessage = errorResponse.data.message;
        } else if (errorResponse.data?.payload?.message) {
          errorMessage = errorResponse.data.payload.message;
        } else if (errorResponse.data?.error) {
          errorMessage = errorResponse.data.error;
        } else if (errorResponse.message) {
          errorMessage = errorResponse.message;
        }

        setError('loginResult', {
          message: errorMessage,
        });
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      setError('loginResult', {
        message: 'Google sign-in failed. Please try again.',
      });
    }
  };

  const handleGoogleError = () => {
    setError('loginResult', {
      message: 'Google sign-in failed. Please try again.',
    });
  };

  // 2FA verification handler - supports regular, Microsoft SSO, and Okta SSO flows
  const handle2FAVerification = async () => {
    if (!pendingLoginData && !pendingMicrosoftIdToken && !pendingOktaIdToken) {
      return;
    }

    if (!otp && !recoveryCode) {
      setTwoFAError('Please enter OTP or recovery code');
      return;
    }

    try {
      // Microsoft SSO 2FA flow
      if (pendingMicrosoftIdToken) {
        const result = await microsoftSignIn({
          idToken: pendingMicrosoftIdToken,
          otpToken: otp || undefined,
          recoveryCode: recoveryCode || undefined,
        });

        if ('data' in result && result.data?.success) {
          // Keep modal open with loading state during full page navigation
          // so the user sees a loading indicator until the page transitions
          const params = new URLSearchParams(window.location.search);
          const invitation = params.get('invitation');
          if (invitation) {
            window.location.href = `/accept-invitation?token=${invitation}`;
          } else {
            window.location.href = '/select-organization';
          }
        } else if ('error' in result) {
          const errData = (result.error as any)?.data;
          const message =
            errData?.message ||
            errData?.error ||
            'Invalid OTP or recovery code. Please try again.';
          setOtp('');
          setRecoveryCode('');
          setTwoFAError(message);
        }
        return;
      }

      // Okta SSO 2FA flow
      if (pendingOktaIdToken) {
        const result = await oktaSignIn({
          idToken: pendingOktaIdToken,
          otpToken: otp || undefined,
          recoveryCode: recoveryCode || undefined,
        });

        if ('data' in result && result.data?.success) {
          const params = new URLSearchParams(window.location.search);
          const invitation = params.get('invitation');
          if (invitation) {
            window.location.href = `/accept-invitation?token=${invitation}`;
          } else {
            window.location.href = '/select-organization';
          }
        } else if ('error' in result) {
          const errData = (result.error as any)?.data;
          const message =
            errData?.message ||
            errData?.error ||
            'Invalid OTP or recovery code. Please try again.';
          setOtp('');
          setRecoveryCode('');
          setTwoFAError(message);
        }
        return;
      }

      // Regular login 2FA flow
      if (!pendingLoginData) {
        return;
      }

      const result = await signIn({
        username: pendingLoginData.username,
        password: pendingLoginData.password,
        otpToken: otp || undefined,
        recoveryCode: recoveryCode || undefined,
      });

      if ('data' in result) {
        const { data } = result;

        if (!data.email_verified) {
          setTwoFAError('User email not verified');
        } else if (!data.is_active) {
          setTwoFAError('Your account is not activated yet by the admin.');
        } else {
          // Success - close modal and navigate
          setShow2FAModal(false);
          setOtp('');
          setRecoveryCode('');
          setTwoFAError('');
          setPendingLoginData(null);

          await navigateAfterLogin();
        }
      } else if ('error' in result) {
        const errData = (result.error as any)?.data;
        const message =
          errData?.message ||
          errData?.error ||
          'Invalid OTP or recovery code. Please try again.';
        setOtp('');
        setRecoveryCode('');
        setTwoFAError(message);
      }
    } catch (error) {
      setOtp('');
      setRecoveryCode('');
      setTwoFAError('An error occurred. Please try again.');
    }
  };

  const handleClose2FAModal = () => {
    setShow2FAModal(false);
    setOtp('');
    setRecoveryCode('');
    setTwoFAError('');
    setPendingLoginData(null);
    setPendingMicrosoftIdToken(null);
    setPendingOktaIdToken(null);
  };

  // Clear 2FA error when user starts typing a new code
  const handle2FAErrorClear = () => {
    if (twoFAError) {
      setTwoFAError('');
    }
  };

  const handleEmailContinue = async () => {
    const emailValue = (getValues('email') ?? '').trim();
    if (!emailValue) {
      setError('email', { message: 'Email is required' });
      return;
    }

    clearErrors('loginResult');
    setIsDiscovering(true);

    try {
      const result = await fetchSsoDiscover(emailValue).unwrap();
      setDiscoveredProviders(result.providers ?? []);
    } catch {
      // Discovery failed — fall back to the password form
      setDiscoveredProviders([]);
    } finally {
      setDiscoveredEmail(emailValue);
      setSsoStep('credentials');
      setIsDiscovering(false);
    }
  };

  const handleBackToEmail = () => {
    setSsoStep('email');
    setDiscoveredProviders([]);
    setDiscoveredEmail('');
    clearErrors('loginResult');
  };

  const hasDiscoveredOkta = discoveredProviders.some((p) => p.type === 'okta');
  const hasDiscoveredMicrosoft = discoveredProviders.some(
    (p) => p.type === 'microsoft'
  );
  const discoveredOktaConfig = discoveredProviders.find(
    (p) => p.type === 'okta'
  )?.config;
  const discoveredMicrosoftConfig = discoveredProviders.find(
    (p) => p.type === 'microsoft'
  )?.config;

  // SSO providers are only offered when the entered email domain is registered
  // to an organization that configured them. Password sign-in stays available
  // either way; Google is reserved for non-SSO domains.
  const hasDiscoveredSso = hasDiscoveredOkta || hasDiscoveredMicrosoft;

  const onSubmit = async (input: InputData) => {
    try {
      const result = await signIn({
        username: input.email,
        password: input.password,
      });

      if ('data' in result) {
        const { data } = result;

        if (!data.email_verified) {
          setError('loginResult', { message: 'User email not verified' });
        } else if (!data.is_active) {
          setError('loginResult', {
            message: 'Your account is not activated yet by the admin.',
          });
        } else if (data.otpEnabled) {
          setPendingLoginData({
            username: input.email,
            password: input.password,
          });
          setShow2FAModal(true);
        } else {
          navigateAfterLogin();
        }
      } else if ('error' in result) {
        const errorResponse = result.error as {
          data?: {
            error?: string;
            message?: string;
            payload?: { message?: string };
          };
          status?: number;
          message?: string;
        };

        // Extract the specific error message from different possible locations
        let errorMessage = 'An error occurred. Please try again.';

        if (errorResponse.data?.message) {
          errorMessage = errorResponse.data.message;
        } else if (errorResponse.data?.payload?.message) {
          errorMessage = errorResponse.data.payload.message;
        } else if (errorResponse.data?.error) {
          errorMessage = errorResponse.data.error;
        } else if (errorResponse.message) {
          errorMessage = errorResponse.message;
        }

        if (
          errorResponse.status === 401 ||
          errorMessage === 'Invalid credentials'
        ) {
          setError('loginResult', {
            message: 'Incorrect username or password.',
          });
        } else {
          setError('loginResult', {
            message: errorMessage,
          });
        }
      }
    } catch (error) {
      setError('loginResult', {
        message: 'Connection error. Please try again.',
      });
    }
  };
  return (
    <>
      {googleClientId ? (
        <SmallForm
          className={className}
          title={
            <Arranger direction="column" alignItems="center">
              <div className="logo-container">
                <LogoSelector prefersDarkMode={prefersDarkMode} />
              </div>
              <Spacer mb={spc.xs} />
              <Text sz="body16" sx={{ pt: spc.xxxs }}>
                Welcome back
              </Text>
            </Arranger>
          }
        >
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <TextField
              {...register('email', {
                onChange(event) {
                  clearErrors('loginResult');

                  if (ssoStep !== 'credentials') {
                    return;
                  }
                  const nextEmail = (event?.target?.value ?? '')
                    .trim()
                    .toLowerCase();
                  const previousEmail = discoveredEmail.trim().toLowerCase();
                  if (nextEmail && nextEmail === previousEmail) {
                    return;
                  }
                  setSsoStep('email');
                  setDiscoveredProviders([]);
                },
              })}
              {...muiError(errors, 'email')}
              required
              fullWidth
              label="Email Address"
              autoComplete="email"
              sx={{ pb: spc.between_vertical_s }}
              startAdornment={<EmailIcon />}
            />

            {ssoStep === 'email' && (
              <>
                <Spacer mb={spc.xl} />
                <Button
                  loading={isDiscovering}
                  onClick={handleEmailContinue}
                  type="button"
                  fullWidth
                  sx={{ mt: spc.xs, mb: spc.between_vertical_s }}
                >
                  Continue
                </Button>
              </>
            )}

            {ssoStep === 'credentials' && (
              <>
                <input {...register('loginResult')} hidden />

                {/* Password field — always available, directly under email */}
                <TextField
                  {...register('password', {
                    onChange() {
                      clearErrors('loginResult');
                    },
                  })}
                  {...muiError(errors, 'password')}
                  required
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="off"
                  sx={{ pb: spc.between_vertical_s }}
                  startAdornment={<LockIcon />}
                  endAdornment={
                    <IconButton onClick={toggleShowPassword}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  }
                />
                <ErrorMessage
                  as={<Text color="error" />}
                  errors={errors}
                  name="loginResult"
                />

                <Spacer mb={spc.xl} />
                <Button
                  loading={isLoading}
                  type="submit"
                  fullWidth
                  sx={{ mt: spc.xs, mb: spc.between_vertical_s }}
                >
                  Continue with Password
                </Button>

                {/* SSO providers configured for this email domain */}
                {hasDiscoveredSso && (
                  <>
                    <Spacer mb={spc.l} />
                    <Divider sx={{ my: 2 }}>or</Divider>
                    <Spacer mb={spc.l} />

                    {hasDiscoveredOkta && discoveredOktaConfig && (
                      <OktaLoginButton
                        onError={(errorMessage) => {
                          setError('loginResult', { message: errorMessage });
                        }}
                        on2FARequired={(idToken) => {
                          setPendingOktaIdToken(idToken);
                          setShow2FAModal(true);
                        }}
                        oktaConfig={{
                          clientId: discoveredOktaConfig.clientId || '',
                          issuer: discoveredOktaConfig.issuer || '',
                          domain: discoveredOktaConfig.domain || '',
                        }}
                      />
                    )}
                    {hasDiscoveredMicrosoft && (
                      <MicrosoftLoginButton
                        onError={(errorMessage) => {
                          setError('loginResult', { message: errorMessage });
                        }}
                        on2FARequired={(idToken) => {
                          setPendingMicrosoftIdToken(idToken);
                          setShow2FAModal(true);
                        }}
                        tenantId={discoveredMicrosoftConfig?.tenantId}
                      />
                    )}
                  </>
                )}

                {/* Google is only offered when the domain isn't tied to an
                    organization's identity provider. */}
                {!hasDiscoveredSso && googleClientId && (
                  <>
                    <Spacer mb={spc.l} />
                    <Divider sx={{ my: 2 }} />
                    <Spacer mb={spc.l} />

                    <GoogleOAuthProvider clientId={googleClientId}>
                      <GoogleLoginButton
                        onSuccess={handleGoogleSuccess}
                        onError={handleGoogleError}
                        loading={isGoogleLoading}
                      />
                    </GoogleOAuthProvider>
                  </>
                )}
              </>
            )}
            <Spacer mb={spc.l} />

            {ssoStep === 'credentials' && (
              <>
                <Link
                  component="button"
                  type="button"
                  variant="body1"
                  onClick={handleBackToEmail}
                >
                  ← Use a different email
                </Link>
                <Spacer mb={spc.m} />
              </>
            )}

            <Arranger
              direction="row"
              alignItems="center"
              justify="space-between"
              sx={{ gap: 1 }}
            >
              <Link href="/forgot-password" variant="body1">
                Forgot password?
              </Link>
              <Link href="/sign-up" variant="body1">
                Don't have an account? Sign up
              </Link>
            </Arranger>
            <Spacer mb={spc.m} />
            <Arranger direction="column" alignItems="center">
              <Text sz="body12" sx={{ mb: spc.m }}>
                <Link href="/privacy" target="_blank">
                  Privacy Policy
                </Link>
                {' • '}
                <Link href="/terms" target="_blank">
                  Terms of Use
                </Link>
              </Text>
            </Arranger>
          </form>
        </SmallForm>
      ) : (
        <SignInSkeleton className={className} />
      )}

      {/* 2FA Verification Modal */}
      <VerificationModal
        onClickCb={handle2FAVerification}
        isOpen={show2FAModal}
        otp={otp}
        setOtp={(value) => {
          setOtp(value);
          handle2FAErrorClear();
        }}
        error={twoFAError}
        isLoading={isLoading || isMicrosoftLoading}
        onClose={handleClose2FAModal}
        btnName="Authenticate"
        btnColor="primary"
        recoveryCode={recoveryCode}
        setRecoveryCode={(value) => {
          setRecoveryCode(value);
          handle2FAErrorClear();
        }}
        setError={setTwoFAError}
      />
    </>
  );
};

export const SignIn = muiStyled(SignInImpl)(
  () => css`
    /* Force Google Login button to take full width */
    .google-login-wrapper {
      width: 100%;

      /* Override any Google OAuth button styles */
      & > div {
        width: 100% !important;
      }

      & iframe {
        width: 100% !important;
      }
    }

    .logo-container {
      svg {
        width: 150px;
        height: 50px;
      }
    }
  `
);

export default SignIn;
